import * as React from "react";
import { isEqual } from "lodash";
import { ExportFormatType, ExportGapFilterType } from "../enumerations";
import { RegionSetFilter, SpecialtyFilter, ZipSummariesReportModel } from "../exportModels";
import { NetworkAssessmentAnalysisType, RegionSetStatusType } from "@app/projects/enumerators";
import { AdequacyFilters } from "../../projects/network-assessment/adequacy";
import { ReportSettingsProps, ReportSettingsState } from "./report-settings/reportSettings";
import { ReportPanelBodyBaseState } from "./reportPanelBodyBase";
import * as helpers from "./exportPanelBodyHelpers";
import { SelectFormat, SelectRegionSetFilter, SelectSpecialtyFilter } from "./filters";
import GenerateBody from "./generate-body";
import * as toast from "./toast";
import ExportService from "../exportService";
import { ReportPanelBodyBaseProps } from "./reportPanelBodyBaseProps";

interface ZipSummariesExportsPanelBodyProps extends ReportPanelBodyBaseProps, ReportSettingsProps {
  adequacyFilters: AdequacyFilters;
  projectId: number;
  runConfigurationId: number;
  specialtyGroupCodes: string[];
}

interface ZipSummariesExportsPanelBodyState extends ReportPanelBodyBaseState, ReportSettingsState {
  regionSetFilter: RegionSetFilter;
  sharedUsers: number[];
  specialtyFilter: SpecialtyFilter;
}

export default class ZipSummariesExportsPanelBody extends React.Component<ZipSummariesExportsPanelBodyProps, ZipSummariesExportsPanelBodyState> {
  private readonly testIdSuffix = "zip-summaries-exports-panel";

  constructor(props: ZipSummariesExportsPanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
      ...this.getInitialReportSettingsState(),
    };
  }

  public componentDidUpdate(prevProps: ZipSummariesExportsPanelBodyProps) {
    if (!isEqual(prevProps.reportSettingsList, this.props.reportSettingsList)) {
      const reportSettingsList = this.props.reportSettingsList;
      if (reportSettingsList) {
        const currentReportSettings = reportSettingsList.find(rs => rs.isDefault);
        if (currentReportSettings) {
          this.handleParseReportSettingsJsonToState(currentReportSettings.reportSettingsJson);
        }
        this.setState({
          currentReportSettings,
          reportSettingsList
        })
      }
    }
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    return (
      <>
        <div className="row">
          <div className="col-12 col-md-4">
            <SelectRegionSetFilter
              disabled={this.props.disableRegionFilters}
              onNetworkAssessmentAnalysisTypeChange={this.handleNetworkAssessmentAnalysisTypeChange}
              onRegionSetFilterTypeChange={this.handleRegionSetFilterTypeChange}
              onRegionSetIncludeChange={this.handleRegionSetIncludeChange}
              regionSetFilter={this.state.regionSetFilter}
              singularDisplay={this.props.singularDisplay}
              pluralDisplay={this.props.pluralDisplay}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectSpecialtyFilter
              adequacyAnalysisType="county"
              disabled={this.props.disableSpecialtyGroupFilters}
              onSpecialtyGapFilterChange={this.handleSpecialtyGapFilterChange}
              onSpecialtyIncludeChange={this.handleSpecialtyIncludeChange}
              onExpandableSpecialtyIncludeChange={() => void false}
              showExpandableSpecialtyOption={false}
              specialtyFilter={this.state.specialtyFilter}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectFormat
              availableExportFormats={[ExportFormatType.CSV, ExportFormatType.Pipe, ExportFormatType.Tab]}
              excelDisabled={false}
              exportType="zip-summaries"
              onOutputFormatChange={this.handleOutputFormatChange}
              outputFormat={this.state.outputFormat}
            />
          </div>
        </div>
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={this.state.reportName.length > 0 && this.state.reportName.length < 255}
          showSharedUsers={true}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.updateSharedUsers}
          exportType="ZipSummary"
          projectId={this.props.projectId}
          networkAssessmentAnalysisType={this.state.regionSetFilter.networkAssessmentAnalysisType}
        />
      </>
    );
  }

  private getInitialBodyState = (): ZipSummariesExportsPanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      outputFormat: ExportFormatType.CSV,
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName(this.props.comparedRunConfigurationId !== null ? "Zip Summaries Comparison" : "Zip Summaries"),
      sharedUsers: [],
      specialtyFilter: {
        filterType: ExportGapFilterType.All,
        includeFacilitySpecialties: true,
        includePhysicianSpecialties: true,
      },
    } as ZipSummariesExportsPanelBodyState;
  }

  private getInitialReportSettingsState = (): ReportSettingsState => {
    return {
      createReportSettingsNameInputDisabled: true,
      currentReportSettings: undefined,
      isSelectionValid: true,
      reportSettingsHasChanges: false,
      reportSettingsHasInvalidChanges: false,
      reportSettingsList: [],
      saveClicked: false,
    } as ReportSettingsState;
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess, reportSettingsHasChanges: true });
  }

  private handleGenerateClick = async () => {
    const requestModel: ZipSummariesReportModel = {
      changedGapsOnly: !!this.props.adequacyFilters.changedGaps,
      comparedProjectRunConfigurationId: this.props.comparedRunConfigurationId,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      networkAssessmentAnalysisTypes: [this.state.regionSetFilter.networkAssessmentAnalysisType],
      projectRunConfigurationId: this.props.runConfigurationId,
      projectUrl: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      sharedUsers: this.state.sharedUsers,
      specialtyGapFilter: this.state.specialtyFilter.filterType,
      specialtyGroupCodes: this.props.specialtyGroupCodes
    };

    const exportService = new ExportService();
    exportService.postExportZipSummaries(requestModel).then(() => {
      toast.toastInfo(this.state.reportName);
    });
  };

  private handleNetworkAssessmentAnalysisTypeChange = (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        networkAssessmentAnalysisType,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat, reportSettingsHasChanges: true });
  }

  private handleParseReportSettingsJsonToState = (reportSettingsJson: string) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);
    if (settings) {
      this.setReportSettingsIsSelectionValid(true);
      this.setState((prevState) => ({
        ...prevState,
        ...settings,
      }));
    }
    else {
      const initialBodyState = this.getInitialBodyState();
      this.setReportSettingsIsSelectionValid(false);
      this.setState((prevState) => ({
        ...prevState,
        ...initialBodyState
      }));
    }
  }

  private handleRegionSetFilterTypeChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        filterType,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private handleRegionSetIncludeChange = (selected: boolean, regionSetStatus: RegionSetStatusType) => {
    let activeSelected: boolean = this.state.regionSetFilter.includeActive;
    let approvedSelected: boolean = this.state.regionSetFilter.includeApproved;
    let pendingSelected: boolean = this.state.regionSetFilter.includePending;

    switch (regionSetStatus) {
      case RegionSetStatusType.Active:
        activeSelected = selected;
        break;
      case RegionSetStatusType.Approved:
        approvedSelected = selected;
        break;
      case RegionSetStatusType.Pending:
        pendingSelected = selected;
        break;
    }

    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        includeActive: activeSelected,
        includeApproved: approvedSelected,
        includePending: pendingSelected,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleSpecialtyGapFilterChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        filterType,
      },
    }));
  }

  private handleSpecialtyIncludeChange = (selected: boolean, isPhysician: boolean) => {

    let physiciansSelected: boolean = this.state.specialtyFilter.includePhysicianSpecialties;
    let facilitiesSelected: boolean = this.state.specialtyFilter.includeFacilitySpecialties;

    if (isPhysician) {
      physiciansSelected = selected;
    } else {
      facilitiesSelected = selected;
    }

    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeFacilitySpecialties: facilitiesSelected,
        includePhysicianSpecialties: physiciansSelected,
      },
    }));
  }

  private setReportSettingsIsSelectionValid = (isSelectionValid: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isSelectionValid,
    }))
  }

  private updateSharedUsers = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  }
}
